import { useEffect, useMemo, useState } from 'react';
import CustomButton from '../../button/button';
import { availableLanguages } from '../../data/languageData';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import successToast from '../../toasts/successToast';
import errorToast from '../../toasts/errorToast';
import { getPptxTextBlocksAction, updatePptxFileAction } from '../../../redux/actions/translationAction';
import { Spinner, Table } from 'react-bootstrap';
import PptxSourceTextBlock from './pptx-source-text';
import PptxContentBlock from './pptx-text-block';
import { filterUpdatedTextblocks } from '../../helper';

const PptxEditor = ({ documentData, sourceFileUrl, translatedFileUrl, job_id, isLoading }) => {
    const [pptxTextBlocks, setPptxTextBlocks] = useState([]);
    const [translatedTextBlocks, setTranslatedTextBlocks] = useState([]);
    const [sourceTextBlocks, setSourceTextBlocks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editedHistory, setEditedHistory] = useState(new Map());
    const [selectedId, setSelectedId] = useState(null);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        if (translatedFileUrl) {
            setLoading(true);
            dispatch(getPptxTextBlocksAction(
                { translatedFileUrl, sourceFileUrl },
                onFetchSuccess,
                onError
            ));
        }
    }, [translatedFileUrl]);

    const groupBySlide = (textBlocks) => {
        const slideMap = new Map();
        textBlocks.forEach(block => {
            if (!block.text || block.text.trim() === "") return;

            const slideId = block.slide;
            if (!slideMap.has(slideId)) {
                slideMap.set(slideId, []);
            }
            slideMap.get(slideId).push(block);
        });
        return Array.from(slideMap.values());
    };

    const groupByShape = (slideBlocks) => {
        return slideBlocks.map(blocks => {
            const shapeMap = new Map();
            blocks.forEach(block => {
                if (!block.text || block.text.trim() === "") return;

                const shapeId = block.shapeId;
                if (!shapeMap.has(shapeId)) {
                    shapeMap.set(shapeId, []);
                }
                shapeMap.get(shapeId).push(block);
            });
            return Array.from(shapeMap.values());
        });
    };

    const groupedTranslatedSlides = useMemo(() => {
        const slides = groupBySlide(translatedTextBlocks);
        return groupByShape(slides);
    }, [translatedTextBlocks]);

    const groupedSourceSlides = useMemo(() => {
        const slides = groupBySlide(sourceTextBlocks);
        return groupByShape(slides);
    }, [sourceTextBlocks]);

    const onFetchSuccess = (data) => {
        const { translatedTextBlocks, sourceTextBlocks } = data.data.data;
        setPptxTextBlocks(translatedTextBlocks);
        setTranslatedTextBlocks(translatedTextBlocks);
        setSourceTextBlocks(sourceTextBlocks);
        setLoading(false);
    };

    const handleTextBlockUpdate = (slideIndex, shapeIndex, updatedBlocks) => {
        setPptxTextBlocks(prev => {
            const newBlocks = [...prev];

            updatedBlocks.forEach(updatedBlock => {
                const originalIndex = newBlocks.findIndex(b => b.id === updatedBlock.id);

                if (originalIndex !== -1) {
                    // Only proceed if text actually changed
                    if (newBlocks[originalIndex].text !== updatedBlock.text) {
                        // Find original translated text (before any edits)
                        const originalTranslatedBlock = translatedTextBlocks.find(b => b.id === updatedBlock.id);

                        // Find corresponding source text
                        const sourceBlock = sourceTextBlocks.find(b => b.id === updatedBlock.id);

                        // Update history
                        setEditedHistory(prevHistory => {
                            const newHistory = new Map(prevHistory);
                            newHistory.set(updatedBlock.id, {
                                id: updatedBlock.id,
                                source: sourceBlock?.text || '',
                                translated: originalTranslatedBlock?.text || '',
                                updated: updatedBlock.text,
                                slide: updatedBlock.slide,
                                shapeId: updatedBlock.shapeId
                            });
                            return newHistory;
                        });

                        // Update the block while preserving all original properties
                        newBlocks[originalIndex] = {
                            ...newBlocks[originalIndex], // Keep all original properties
                            text: updatedBlock.text     // Only update the text
                        };
                    }
                }
            });

            return newBlocks;
        });
    };

    const handleSubmit = () => {
        setLoading(true);
        const updatedParts = Array.from(editedHistory.values());
        const filteredUpdatedParts = filterUpdatedTextblocks(updatedParts);
        if (updatedParts.length > 0) {
            dispatch(updatePptxFileAction({
                documentUrl: translatedFileUrl,
                updatedTextBlocks: pptxTextBlocks,
                updatedParts: filteredUpdatedParts,
                outputPath: `${job_id}.pptx`,
                job_id
            }, onSuccess, onError));
        } else {
            navigate('/post-edit/summary', { state: { job_id } });
        }
    };

    const handleSaveDraft = () => {
        setLoading(true);
        const updatedParts = Array.from(editedHistory.values());
        const filteredUpdatedParts = filterUpdatedTextblocks(updatedParts);
        dispatch(updatePptxFileAction({
            documentUrl: translatedFileUrl,
            updatedTextBlocks: pptxTextBlocks,
            updatedParts: filteredUpdatedParts,
            outputPath: `${job_id}.pptx`,
            job_id
        }, onSaveSuccess, onError));
    };

    const onSuccess = (data) => {
        setLoading(false);
        successToast(data.data.message);
        const job = data.data.data;
        const updatedParts = Array.from(editedHistory.values());
        navigate('/post-edit/summary', {
            state: {
                updatedParts,
                sourceLang: job.source_lang,
                targetLang: job.target_lang,
                job_id
            }
        });
        setEditedHistory(new Map());
    };

    const onSaveSuccess = (data) => {
        // setLoading(false);
        setEditedHistory(new Map());
        successToast(data.data.message);
        dispatch(getPptxTextBlocksAction(
            { translatedFileUrl: data?.data?.data?.signed_url, sourceFileUrl },
            onFetchSuccess,
            onError
        ));
    };

    const onError = (data) => {
        setLoading(false);
        errorToast(data?.data?.message);
    };

    const slideIds = useMemo(() => {
        const ids = new Set();
        translatedTextBlocks.forEach(block => ids.add(block.slide));
        return Array.from(ids).sort();
    }, [translatedTextBlocks]);

    return (
        <div className='bg-white h-100 border rounded-4 m-2'>
            <div className='d-flex justify-content-between w-100 align-items-center p-2'>
                <div className='d-flex gap-4 w-100 align-items-center'>
                    <CustomButton
                        disabled={true}
                        variant='primary'
                        className='d-flex align-items-center cursor_pointer fs_12'
                        style={{ height: '30px' }}
                    >
                        {availableLanguages.find(lang => lang?.value === documentData?.source_lang)?.label ?? "Loading..."}
                    </CustomButton>
                </div>
                <div className='d-flex justify-content-between gap-4 w-100 align-items-center'>
                    <div className='px-3'>
                        <CustomButton
                            disabled={true}
                            variant='primary'
                            className='d-flex align-items-center cursor_pointer fs_12'
                            style={{ height: '30px' }}
                        >
                            {availableLanguages.find(lang => lang?.value === documentData?.target_lang)?.label ?? "Loading..."}
                        </CustomButton>
                    </div>
                    <div className='d-flex gap-3'>
                        <CustomButton variant='primary' disabled={loading} className='d-flex align-items-center fs_12' style={{ height: '30px' }} onClick={handleSaveDraft}>
                            Save Draft
                        </CustomButton>
                        <CustomButton variant='primary' disabled={loading} className='d-flex align-items-center fs_12' style={{ height: '30px' }} onClick={handleSubmit}>
                            Submit For Review
                        </CustomButton>
                    </div>
                </div>
            </div>

            <div className='border-top' style={{ height: "80vh", overflowY: "auto" }}>
                {(isLoading || loading) ? (
                    <div className="d-flex justify-content-around align-items-center" style={{ height: "100%" }}>
                        <Spinner animation="border" variant="primary" />
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <div style={{ padding: '5x' }}>
                        {[...slideIds].sort((a, b) => a - b).map((slideId) => {
                            const slideIndex = slideIds.indexOf(slideId);

                            return (
                                <div key={`slide-${slideId}`} >
                                    <Table borderless className="mb-0" style={{ borderCollapse: 'separate', borderSpacing: '5px' }}>
                                        <tbody >
                                            {groupedSourceSlides[slideIndex]?.map((sourceShapes, shapeIndex) => {
                                                const translatedShapes = groupedTranslatedSlides[slideIndex]?.[shapeIndex] || [];
                                                return (
                                                    <tr key={`row-${slideIndex}-${shapeIndex}`}>
                                                        <td style={{ width: '50%', verticalAlign: "top", border: "1px solid #dee2e6", backgroundColor: selectedId === `${slideIndex}-${shapeIndex}` ? "rgb(236, 243, 251)" : "#ffff" }}>
                                                            <PptxSourceTextBlock
                                                                key={`source-${slideIndex}-${shapeIndex}`}
                                                                block={sourceShapes}
                                                            />
                                                        </td>
                                                        <td
                                                            style={{
                                                                width: '50%',
                                                                verticalAlign: "top",
                                                                border: "1px solid #dee2e6",
                                                                padding: '8px',
                                                                backgroundColor: selectedId === `${slideIndex}-${shapeIndex}` ? "rgb(236, 243, 251)" : "#ffff"
                                                            }}
                                                            onClick={() => setSelectedId(`${slideIndex}-${shapeIndex}`)}
                                                        >
                                                            {translatedShapes.length > 0 ? (
                                                                <PptxContentBlock
                                                                    isSelected={selectedId === `${slideIndex}-${shapeIndex}`}
                                                                    slideIndex={slideIndex}
                                                                    shapeIndex={shapeIndex}
                                                                    key={`translated-${slideIndex}-${shapeIndex}`}
                                                                    onUpdate={handleTextBlockUpdate}
                                                                    block={translatedShapes}
                                                                />
                                                            ) : (
                                                                <div style={{ minHeight: '40px' }}></div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PptxEditor;